import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import CustomerStories from "@/components/marketing/CustomerStories";
import AboutHero from "@/components/marketing/AboutHero";
import AboutCapabilities from "@/components/marketing/AboutCapabilities";
import AboutBeliefs from "@/components/marketing/AboutBeliefs";
import AboutHowItWorks from "@/components/marketing/AboutHowItWorks";
import StarField from "@/components/marketing/StarField";
import CodeRain from "@/components/marketing/CodeRain";
import { useCodeRain } from "@/lib/siteEffects";

export default function About() {
  const rain = useCodeRain("about");
  return (
    <div>
      {/* 1. HERO — clean two-zone composition, navy bg matching buy-leads/footer */}
      <AboutHero />

      {/* 2. CAPABILITIES — dark navy band carrying the brand from the hero */}
      <AboutCapabilities />

      {/* 3. WHAT WE BELIEVE — premium light section with balanced 3-pillars */}
      <AboutBeliefs />

      {/* 4. HOW IT WORKS — dark navy stage cards with KPI strip */}
      <AboutHowItWorks />

      {/* 5. WHERE WE OPERATE — refined city cards on light bg */}
      <section className="relative overflow-hidden py-24 lg:py-32 bg-white border-t border-slate-200" data-testid="about-locations">
        {rain.on && <CodeRain variant="light" intensity={rain.intensity} />}
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">Where we operate</p>
              <h2 className="mt-3 text-4xl sm:text-5xl lg:text-[56px] tracking-[-0.035em] font-semibold text-slate-900 leading-[1.02]">
                Four offices. One operating layer.
              </h2>
            </div>
            <p className="text-[15px] text-slate-500 max-w-md leading-relaxed">
              Lean by design. Every team member sits within two doors of a routing engineer and a compliance lead.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { c: "London", l: "Engineering · HQ", code: "LON", flag: "🇬🇧" },
              { c: "Manchester", l: "Operations · Buyer success", code: "MAN", flag: "🇬🇧" },
              { c: "Dublin", l: "EU compliance", code: "DUB", flag: "🇮🇪" },
              { c: "Lisbon", l: "AI research", code: "LIS", flag: "🇵🇹" },
            ].map((p, i) => (
              <div
                key={i}
                className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#0055FF]/40 hover:-translate-y-0.5 hover:shadow-md transition-all"
                data-testid={`location-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[28px] leading-none drop-shadow-sm" aria-hidden>{p.flag}</span>
                  <span className="text-[10px] font-mono tracking-widest text-slate-400 group-hover:text-[#0055FF]/70 transition-colors">{p.code}</span>
                </div>
                <div className="mt-12 text-[10.5px] uppercase tracking-[0.2em] text-slate-500 font-medium">{p.l}</div>
                <div className="mt-1.5 text-2xl tracking-[-0.025em] font-semibold text-slate-900">{p.c}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. LIVE CUSTOMER STORIES */}
      <CustomerStories
        heading="Live numbers from real Connexis client activity."
        subheading="The same MongoDB-backed metrics your team uses inside the platform — surfaced here in real time."
        maxCards={6}
      />

      {/* 7. FINAL CTA — dark bookend matching the hero */}
      <section className="relative overflow-hidden bg-[#0F172A] text-white py-28" data-testid="about-final-cta">
        <StarField opacity={0.45} />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#7da9ff]">Get connected</p>
          <h2 className="mt-3 text-4xl sm:text-5xl lg:text-6xl tracking-[-0.04em] font-semibold leading-[1.02]">
            See the platform from{" "}
            <span className="bg-gradient-to-r from-white via-[#9cc4ff] to-[#3d7bff] bg-clip-text text-transparent">
              the inside.
            </span>
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-[16px] text-white/60 leading-relaxed">
            30-minute walkthrough with an operator. Bring your numbers — we'll show you how the network would price and route them.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/book-demo">
              <Button data-testid="about-cta" className="rounded-full bg-white text-slate-900 hover:bg-[#3d7bff] hover:text-white px-5 h-11 text-[14.5px] font-semibold shadow-[0_8px_28px_rgba(255,255,255,0.18)]">
                Book strategy call <ArrowRight className="size-4" />
              </Button>
            </Link>
            <Link to="/buy-leads">
              <Button variant="outline" className="rounded-full border-white/20 hover:border-white/55 bg-transparent text-white hover:bg-white/[0.05] px-5 h-11 text-[14.5px] font-medium">
                Or buy leads now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
